import { useEffect, useState } from "react";
import { seo, breadcrumbScript } from "@/lib/seo";
import { Link, createFileRoute, notFound } from "@tanstack/react-router";
import { ArrowLeft, Bookmark } from "lucide-react";
import { SIMULATIONS } from "@/lib/simulations/registry";
import type { SimDefinition } from "@/lib/simulations/types";
import {
  pushRecent,
  readBookmarks,
  readNote,
  saveNote,
  setProgress,
  toggleBookmark,
} from "@/lib/simulations/storage";
import { SimPlayer } from "@/components/simulations/sim-player";
import { SimulationGate } from "@/components/billing/simulation-gate";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/simulations/$simId")({
  loader: ({ params }) => {
    const meta = SIMULATIONS.find((s) => s.id === params.simId);
    if (!meta) throw notFound();
    const { load: _load, ...rest } = meta;
    return rest;
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return {
        meta: [{ title: "Simulation not found" }, { name: "robots", content: "noindex" }],
      };
    }
    const title = `${loaderData.title} Simulation — The Quantum Minds`;
    return {
      ...seo({
        title,
        description: loaderData.blurb,
        path: `/simulations/${params.simId}`,
        type: "article",
      }),
      scripts: [
        breadcrumbScript([
          { name: "Home", path: "/" },
          { name: "Simulations", path: "/simulations" },
          { name: loaderData.title, path: `/simulations/${params.simId}` },
        ]),
      ],
    };
  },
  component: SimulationPage,
  notFoundComponent: SimNotFound,
});

function SimNotFound() {
  return (
    <div className="page-shell px-5 lg:px-8 py-16 text-center">
      <p className="font-semibold">That simulation doesn&apos;t exist</p>
      <Link to="/simulations" className="mt-3 inline-block text-sm font-semibold text-primary">
        Back to the hub
      </Link>
    </div>
  );
}

function SimulationPage() {
  const meta = Route.useLoaderData();
  const [def, setDef] = useState<SimDefinition | null>(null);
  const [note, setNote] = useState("");
  const [bookmarks, setBookmarks] = useState<string[]>([]);

  useEffect(() => {
    let alive = true;
    setDef(null);
    const entry = SIMULATIONS.find((s) => s.id === meta.id);
    entry?.load().then((mod) => {
      if (alive) setDef(mod.default);
    });
    pushRecent(meta.id);
    setProgress(meta.id, 25);
    setNote(readNote(meta.id));
    setBookmarks(readBookmarks());
    return () => {
      alive = false;
    };
  }, [meta.id]);

  const bookmarked = bookmarks.includes(meta.id);

  return (
    <div className="page-shell px-5 lg:px-8 pb-6 pt-8">
      <div className="flex items-center gap-3">
        <Link
          to="/simulations"
          aria-label="Back to simulations"
          className="glass grid size-9 shrink-0 place-items-center rounded-full transition-transform active:scale-95"
        >
          <ArrowLeft className="size-4" />
        </Link>
        <div className="min-w-0 flex-1">
          <p className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
            {meta.subject} · {meta.chapter}
          </p>
          <h1 className="truncate text-[19px] font-extrabold tracking-tight">{meta.title}</h1>
        </div>
        <button
          type="button"
          aria-label={bookmarked ? "Remove from saved" : "Save simulation"}
          onClick={() => setBookmarks(toggleBookmark(meta.id))}
          className="glass grid size-9 shrink-0 place-items-center rounded-full transition-transform active:scale-90"
        >
          <Bookmark
            className={cn("size-4", bookmarked ? "fill-primary text-primary" : "text-muted-foreground")}
          />
        </button>
      </div>

      <div className="mt-5">
        <SimulationGate simulationId={meta.id}>
        {def ? (
          <SimPlayer
            def={def}
            note={note}
            onNoteChange={(text) => {
              setNote(text);
              saveNote(meta.id, text);
              setProgress(meta.id, 100);
            }}
          />
        ) : (
          <div className="space-y-3">
            <div className="glass animate-shimmer h-[280px] rounded-[22px]" />
            <div className="glass animate-shimmer h-32 rounded-[20px]" />
          </div>
        )}
        </SimulationGate>
      </div>
    </div>
  );
}